<template>
  <div class="app-wrapper">
    <div v-show="!showCamera && !showCameraStream">
      <v-toolbar class="d-flex justify-center">
        <v-btn
          icon
          @click="goBack"
        >
          <v-icon>mdi-reply</v-icon>
        </v-btn>
        <v-btn
          icon
          @click="removeHistoryStroke"
        >
          <v-icon>mdi-eraser-variant</v-icon>
        </v-btn>
        <v-btn
          icon
          @click="removeAllHistory"
        >
          <v-icon>mdi-delete</v-icon>
        </v-btn>
        <v-speed-dial
          v-model="fabSize"
          direction="bottom"
        >
          <template v-slot:activator>
            <v-btn
              v-model="fabSize"
              icon
            >
              <v-icon v-if="fabSize">
                mdi-close
              </v-icon>
              <v-icon v-else>
                mdi-pencil
              </v-icon>
            </v-btn>
          </template>
          <v-btn
            v-for="(sizeItem, index) in sizes"
            :key="index"
            icon
            class="size-item"
            @click="size = sizeItem"
          >
            <span
              class="size"
              :style="{ width: sizeItem + 'px', height: sizeItem + 'px' }"
            />
          </v-btn>
        </v-speed-dial>
        <v-speed-dial
          v-model="fabColor"
          direction="bottom"
        >
          <template v-slot:activator>
            <v-btn
              v-model="fabColor"
              icon
              dark
              :color="color"
            >
              <v-icon v-if="fabColor">
                mdi-close
              </v-icon>
              <v-icon v-else>
                mdi-palette
              </v-icon>
            </v-btn>
          </template>
          <v-btn
            v-for="(colorItem, index) in colors"
            :key="index"
            icon
            class="size-item"
            @click="color = colorItem"
          >
            <span
              class="size"
              :style="{
                width: 48 + 'px',
                height: 48 + 'px',
                background: colorItem
              }"
            />
          </v-btn>
        </v-speed-dial>
        <v-btn
          icon
          @click="onSave"
        >
          <v-icon>mdi-content-save</v-icon>
        </v-btn>
        <v-btn
          v-if="!backgroundImage"
          icon
          @click="showCameraStream = true"
        >
          <v-icon>mdi-camera-outline</v-icon>
        </v-btn>
      </v-toolbar>

      <canvas
        ref="canvas"
        width="768"
        height="830"
        class="canvas"
        @mousedown="downEvent"
        @mouseleave="leaveEvent"
        @mouseup="upEvent"
        @mousemove="moveEvent"
        @touchstart.prevent="downEvent"
        @touchcancel.prevent="leaveEvent"
        @touchend.prevent="upEvent"
        @touchmove.prevent="moveEvent"
      />
    </div>

    <CameraStream
      v-if="showCameraStream"
      camera="front"
      @capture="getScreenshot"
    />

    <img
      v-show="false"
      ref="bgimg"
      :src="capturedImage"
    >
  </div>
</template>

<script>
import CameraStream from "./CameraStream";

export default {
  name: "DrawView",

  components: {
    CameraStream
  },

  props: {
    // Set background image
    backgroundImage: {
      type: String,
      default: ""
    },

    // Set value (points cordinates)
    value: String
  },

  data() {
    return {
      // Flag to show Camera component
      showCamera: false,

      showCameraStream: false,

      // List of points
      history: [],

      // Available colors
      colors: ["#13f7ab", "#138cf7", "#f71313"],

      // Selected color
      color: "#138cf7",

      // Flag for colors btn fab
      fabColor: false,

      // Available brushsize
      sizes: [6, 12, 24, 48],

      // Selected brushsize
      size: 6,

      // Flag for brushsizes btn fab
      fabSize: false,

      // How many touch event to skip
      modulo: 2,

      // Captured image
      capturedImage: null,

      imageBlob: null
    };
  },

  mounted() {
    this.decodeBase64();
    this.init();
  },

  methods: {
    /**
     * Convert Base64 data to raw binary data
     */
    dataURItoBlob(dataURI) {
      let byteString;

      if (dataURI.split(",")[0].indexOf("base64") >= 0)
        byteString = atob(dataURI.split(",")[1]);
      else byteString = unescape(dataURI.split(",")[1]);

      // separate out the mime component
      const mimeString = dataURI
        .split(",")[0]
        .split(":")[1]
        .split(";")[0];

      // write the bytes of the string to a typed array
      let ia = new Uint8Array(byteString.length);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      return new Blob([ia], { type: mimeString });
    },

    onSave() {
      this.$emit("on-save", {
        imageBlob: this.imageBlob,
        image: this.capturedImage,
        sketch: this.encodeToBase64(this.history)
      });
    },

    goBack() {
      this.$emit("go-back");
    },

    // Load an image a return a pomise
    loadImage(imageSource) {
      return new Promise((resolve, reject) => {
        let image = new Image();
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = imageSource;
      });
    },

    /**
     * Triggered
     */
    async getScreenshot(screenshot) {
      this.imageBlob = this.dataURItoBlob(screenshot);
      this.showCamera = false;
      this.showCameraStream = false;
      this.capturedImage = screenshot;
      this.redraw();
    },

    // Encode history to Base64
    encodeToBase64() {
      const history = JSON.stringify(this.history);
      const historyBase64 = btoa(history);
      return historyBase64;
    },

    // Decode history from Base64 to json
    decodeBase64() {
      if (!this.value) {
        return;
      }

      const historyBase64 = this.value;
      const history = atob(historyBase64);
      this.history = JSON.parse(history);
    },

    // Remove a point or two
    removeHistoryItem() {
      this.history.splice(this.history.length - 2, 1);
      this.redraw();
    },

    // Remove a stroke
    removeHistoryStroke() {
      this.history = this.history.filter(x => x.s < this.stroke_counter);
      this.redraw();
      this.$emit("input", this.encodeToBase64());
    },

    // Remove all points
    removeAllHistory() {
      this.history = [];
      this.stroke_counter = 0;
      this.redraw();
      this.$emit("input", this.encodeToBase64());
    },

    // Set canvas size
    // TODO: set dynamic
    setSize() {
      // this.c.width = window.innerWidth;
      // this.c.height = window.innerHeight - 60;
      // this.c.width = 600;
      // this.c.height = 600;
    },

    // Start tracking
    downEvent(e) {
      this.stroke_counter++;
      this.mouseDown = true;

      // If is mouse event
      this.mouseX = e.offsetX;
      this.mouseY = e.offsetY;

      // Check if is touch event
      if (e.touches) {
        this.mouseX =
          e.touches[0].pageX - this.$refs.canvas.getBoundingClientRect().left;
        this.mouseY =
          e.touches[0].pageY - this.$refs.canvas.getBoundingClientRect().top;
      }

      this.setDummyPoint();
    },

    // Remove tracking for mouseup/touchend events
    upEvent() {
      if (this.mouseDown) {
        this.setDummyPoint();
      }
      this.mouseDown = false;
      this.$emit("input", this.encodeToBase64());
    },

    // Leaving zone: remove tracking
    leaveEvent() {
      if (this.mouseDown) {
        this.setDummyPoint();
      }
      this.mouseDown = false;
    },

    // If mouse is down add points to history
    moveEvent(e) {
      if (this.mouseDown) {
        // If is mouse event
        this.mouseX = e.offsetX;
        this.mouseY = e.offsetY;

        // Check if is touch event
        if (e.touches) {
          this.mouseX =
            e.touches[0].pageX - this.$refs.canvas.getBoundingClientRect().left;
          this.mouseY =
            e.touches[0].pageY - this.$refs.canvas.getBoundingClientRect().top;
        }

        if (this.current_modulo < this.modulo) {
          this.current_modulo++;
          return;
        }

        var item = {
          isDummy: false,
          x: this.mouseX,
          y: this.mouseY,
          c: this.color,
          r: this.size,
          s: this.stroke_counter
        };

        this.history.push(item);
        this.draw(item, this.history.length);
      }
    },

    // Get a false point to start and finish lines
    getDummyItem() {
      var lastPoint = this.history[this.history.length - 1];

      if (lastPoint == undefined) {
        lastPoint = { x: this.mouseX, y: this.mouseY };
      }

      return {
        isDummy: true,
        x: lastPoint.x,
        y: lastPoint.y,
        c: null,
        r: null,
        s: this.stroke_counter
      };
    },

    // Get a false point to start and finish lines
    setDummyPoint() {
      var item = this.getDummyItem();
      this.history.push(item);
      this.draw(item, this.history.length);
      this.current_modulo = 0;
    },

    // Clear canvas and redraw all points and images
    async redraw() {
      // Reset canvas
      this.ctx.clearRect(0, 0, this.c.width, this.c.height);

      var bgImg;
      var capturedImg;

      // Check if there is a background image
      if (this.backgroundImage != "") {
        // Draw the image on canvas
        try {
          bgImg = await this.loadImage(this.backgroundImage);

          if (this.backgroundImage.startsWith("http")) {
            this.ctx.drawImage(
              bgImg,
              0,
              0,
              this.c.width,
              (this.c.width * bgImg.height) / bgImg.width
            );
          } else {
            const scale = Math.min(
              this.c.width / bgImg.width,
              this.c.height / bgImg.height
            );

            this.ctx.drawImage(
              bgImg,
              this.c.width / 2 - (bgImg.width / 2) * scale,
              this.c.height / 2 - (bgImg.height / 2) * scale,
              bgImg.width * scale,
              bgImg.height * scale
            );
          }
        } catch (e) {
          console.error(e);
        }
      }

      // Check if there is a captured image
      if (this.capturedImage != null && this.backgroundImage == "") {
        capturedImg = await this.loadImage(this.capturedImage);
        this.ctx.drawImage(capturedImg, 0, 0, this.c.width, this.c.height);
      }

      // If there are no points to draw, we are done
      if (!this.history.length) return;

      // Draw each point on canvas
      this.history.forEach((item, i) => {
        this.draw(item, i);
      });

      // classify on same stroke
      this.stroke_counter = 0;
      var last = this.history[this.history.length - 1];
      if (last != undefined) {
        this.stroke_counter = last.s;
      }
    },

    // Draw a point on canvas based on item
    draw(item, i) {
      this.ctx.lineCap = "round";
      this.ctx.lineJoin = "round";

      // Previous item
      const prevItem = this.history[i - 2];

      // Dont go below index 2
      if (i < 2) {
        return;
      }

      // Start drawing
      if (!item.isDummy && !this.history[i - 1].isDummy && !prevItem.isDummy) {
        this.ctx.strokeStyle = item.c;
        this.ctx.lineWidth = item.r;

        this.ctx.beginPath();
        this.ctx.moveTo(prevItem.x, prevItem.y);
        this.ctx.lineTo(item.x, item.y);
        this.ctx.stroke();
        this.ctx.closePath();
      } else if (!item.isDummy) {
        this.ctx.strokeStyle = item.c;
        this.ctx.lineWidth = item.r;

        this.ctx.beginPath();
        this.ctx.moveTo(item.x, item.y);
        this.ctx.lineTo(item.x, item.y);
        this.ctx.stroke();
        this.ctx.closePath();
      }
    },

    // Initialize canvas
    init() {
      this.c = this.$refs.canvas;
      this.ctx = this.c.getContext("2d");

      this.mouseDown = false;
      this.mouseX = 0;
      this.mouseY = 0;

      this.tempHistory = [];

      this.stroke_counter = 0;

      this.setSize();

      this.redraw();
    }
  }
};
</script>

<style lang="scss">
$prim: rgb(0, 149, 255);

.app-wrapper {
  height: 100%;
  width: 100%;
}

.canvas {
  border: 1px solid #000;
}

.size-item {
  width: 40px;
  height: 60px;
  display: inline-flex;
  position: relative;
  justify-content: center;
  align-items: center;
  vertical-align: top;
  cursor: pointer;

  input {
    position: absolute;
    top: 0;
    left: 0;
    width: 0;
    height: 0;
    opacity: 0;
  }

  .size {
    background-color: rgb(140, 140, 140);
    display: inline-block;
    border-radius: 50%;
    transition: all 0.15s;
    transform: translate(-50%, -50%) scale(0.6);
    position: absolute;
    top: 50%;
    left: 50%;

    &:hover {
      opacity: 0.8;
    }
  }

  input:checked + .size {
    background-color: $prim;
  }
}

.thumb {
  .canvas {
    width: 100%;
    height: 100%;
    position: absolute;
  }

  .image-thumb {
    position: absolute;
    width: 100%;
    height: auto;
  }
}
</style>
